import { create } from 'zustand';
import type { SupportInfo } from '@/lib/utils/gridSearchUi';

/**
 * Represents the severity levels available for snackbar notifications
 */
export type SnackbarSeverity = 'success' | 'info' | 'warning' | 'error';

/**
 * Interface defining the state and actions for the global snackbar notification system
 * @property {boolean} open - Controls the visibility of the snackbar
 * @property {string} message - The notification message to display
 * @property {SnackbarSeverity} severity - The type/severity of the notification
 * @property {number} autoHideDuration - Duration in milliseconds before the snackbar automatically hides
 * @property {Function} showSnackbar - Function to display a snackbar notification
 * @property {Function} hideSnackbar - Function to hide the snackbar notification
 */
interface SnackbarState {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  autoHideDuration: number | null;
  title?: string;
  /** Optional structured support information to show in a details dialog or copy action */
  supportInfo?: SupportInfo;

  // Actions
  showSnackbar: (
    message: string,
    severity?: SnackbarSeverity,
    autoHideDuration?: number | null,
    title?: string,
    supportInfo?: SupportInfo
  ) => void;
  hideSnackbar: () => void;
}

/**
 * Global store for managing snackbar notifications throughout the application
 * Provides a centralized way to show and hide notifications with different severity levels
 *
 * @example
 * // Show a success message
 * useSnackbarStore.getState().showSnackbar('Operation successful', 'success');
 *
 * // Show an error message with custom duration
 * useSnackbarStore.getState().showSnackbar('An error occurred', 'error', 5000);
 *
 * // In a React component, using the Zustand hook:
 * const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
 * showSnackbar('Operation successful', 'success');
 */
export const useSnackbarStore = create<SnackbarState>((set) => ({
  open: false,
  message: '',
  severity: 'info',
  autoHideDuration: 3000,
  title: undefined,
  supportInfo: undefined,

  showSnackbar: (
    message: string,
    severity: SnackbarSeverity = 'info',
    autoHideDuration: number | null = 3000,
    title = undefined,
    supportInfo
  ) => set({ open: true, message, severity, autoHideDuration, title, supportInfo }),

  hideSnackbar: () => set({ open: false }),
}));
